import { Controller } from "stimulus"
import { showToast } from "../utils/toast"
import { detachRow, lowerPagerSummary, refreshSubtitle, shiftStateCount } from "../inbox_list_counts"

interface ForwardResponse {
  recipients: string[]
  forwarded_at: string
  previous_state?: string
  response_state?: string
  flags?: { row: string; drawer: string }
}

interface ForwardDetail {
  chainId: string
  url: string
  candidate: string
  campaign: string
  messageCount: string
  addresses: string[]
  canRemember: boolean
}

export default class extends Controller {
  static targets = [
    "context", "addresses", "empty", "otherHint", "remember", "rememberInput",
    "note", "count", "submit", "submitLabel", "error"
  ]

  declare readonly contextTarget: HTMLElement
  declare readonly addressesTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly otherHintTarget: HTMLElement
  declare readonly rememberTarget: HTMLElement
  declare readonly rememberInputTarget: HTMLInputElement
  declare readonly noteTarget: HTMLTextAreaElement
  declare readonly countTarget: HTMLElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly submitLabelTarget: HTMLElement
  declare readonly errorTarget: HTMLElement

  private url: string | null = null
  private chainId: string | null = null
  private sending = false

  open(event: CustomEvent<ForwardDetail>): void {
    const detail = event.detail
    if (!detail) return

    this.url = detail.url
    this.chainId = detail.chainId || null
    this.sending = false

    this.contextTarget.textContent = [detail.candidate, detail.campaign, detail.messageCount]
      .filter(part => part && part.length > 0)
      .join(" · ")

    this.renderAddresses(detail.addresses || [])

    this.rememberTarget.hidden = !detail.canRemember
    this.rememberInputTarget.checked = false
    this.otherHintTarget.textContent = detail.canRemember
      ? "One-off recipients. Tick “Remember” to add them to this bot's forward-to list."
      : "One-off recipients, used for this forward only."

    this.noteTarget.value = ""
    this.scopeInputs().forEach(input => { input.checked = input.value === "thread" })
    this.clearOther()
    this.clearError()
    this.refresh()

    this.element.classList.add("open")
    this.element.setAttribute("aria-hidden", "false")
  }

  close(): void {
    if (this.sending) return

    this.element.classList.remove("open")
    this.element.setAttribute("aria-hidden", "true")
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.element) this.close()
  }

  closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape") return
    if (!this.element.classList.contains("open")) return

    this.close()
  }

  // Ticking a chip or committing a one-off address both land here.
  refresh(): void {
    const count = this.recipients().length

    this.countTarget.textContent = count === 0
      ? "No recipients selected"
      : `Forwarding to ${count} recipient${count === 1 ? "" : "s"}`
    this.submitTarget.disabled = count === 0 || this.sending
  }

  submit(): void {
    const recipients = this.recipients()
    if (recipients.length === 0 || this.sending || !this.url) return

    // Captured so the response is handled the way the request was sent, whatever the checkbox
    // does while the forward is in flight.
    const remember = this.rememberInputTarget.checked && !this.rememberTarget.hidden

    this.setSending(true)
    this.clearError()

    fetch(this.url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        recipients,
        scope: this.selectedScope(),
        note: this.noteTarget.value,
        remember
      })
    })
      .then(response => response.json().then(data => {
        if (!response.ok) throw data
        return data as ForwardResponse
      }))
      .then(data => {
        this.setSending(false)
        this.close()
        if (remember) this.rememberAddresses(data.recipients)
        this.addActivity(data.recipients)
        this.applyState(data)
        showToast(`Forwarded to ${data.recipients.join(", ")}`)
      })
      .catch(error => {
        this.setSending(false)
        this.showError(error?.error || "Could not forward this thread. Please try again.")
      })
  }

  // --- internals ---------------------------------------------------------

  // The server has added these to the bot's forward-to list. `open` rebuilds the chips from the
  // drawer button's data-addresses, so that is what has to change for them to show up next time;
  // the drawer re-renders the button from the bot when the thread is fetched again.
  private rememberAddresses(recipients: string[]): void {
    const opener = document.querySelector<HTMLElement>(".inbox-drawer.open .inbox-forward-open")
    if (!opener) return

    let saved: string[] = []
    try {
      saved = JSON.parse(opener.getAttribute("data-addresses") || "[]")
    } catch (e) {
      saved = []
    }

    const addresses = Array.from(new Set([...saved, ...recipients]))
    opener.setAttribute("data-addresses", JSON.stringify(addresses))
    this.renderAddresses(addresses)
  }

  private renderAddresses(addresses: string[]): void {
    this.addressesTarget.innerHTML = ""

    addresses.forEach(address => {
      const label = document.createElement("label")
      label.className = "fwdm-chip"

      const input = document.createElement("input")
      input.type = "checkbox"
      input.value = address
      input.dataset.action = "change->forward-modal#refresh"

      const text = document.createElement("span")
      text.textContent = address

      label.appendChild(input)
      label.appendChild(text)
      this.addressesTarget.appendChild(label)
    })

    this.addressesTarget.hidden = addresses.length === 0
    this.emptyTarget.hidden = addresses.length > 0
  }

  // The ticked chips plus whatever the chip editor holds, deduplicated: an address can be both.
  private recipients(): string[] {
    const ticked = Array.from(
      this.addressesTarget.querySelectorAll<HTMLInputElement>("input[type=checkbox]:checked")
    ).map(input => input.value)

    const other = this.otherSelect()
      ? Array.from(this.otherSelect()!.selectedOptions).map(option => option.value)
      : []

    return Array.from(new Set([...ticked, ...other]))
  }

  private otherSelect(): HTMLSelectElement | null {
    return this.element.querySelector<HTMLSelectElement>("[data-email-chips-target='select']")
  }

  private clearOther(): void {
    const select = this.otherSelect()
    if (!select) return

    select.innerHTML = ""
    // email_chips_controller repaints off this attribute.
    select.dataset.previousValue = "[]"

    const input = this.element.querySelector<HTMLInputElement>("[data-email-chips-target='input']")
    if (input) {
      input.value = ""
      input.classList.remove("is-invalid")
    }
  }

  private scopeInputs(): HTMLInputElement[] {
    return Array.from(this.element.querySelectorAll<HTMLInputElement>("input[name='fwdm-scope']"))
  }

  private selectedScope(): string {
    return this.scopeInputs().find(input => input.checked)?.value || "thread"
  }

  private setSending(sending: boolean): void {
    this.sending = sending
    this.submitTarget.disabled = sending || this.recipients().length === 0
    this.submitLabelTarget.textContent = sending ? "Forwarding…" : "Forward"
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  private clearError(): void {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  // Optimistic: the drawer re-renders the authoritative row from thread_forwards next time it
  // opens, so this only has to match what the server just told us.
  private addActivity(recipients: string[]): void {
    const timeline = document.querySelector(".inbox-drawer.open .inbox-timeline")
    if (!timeline) return

    const note = document.createElement("article")
    note.className = "inbox-forward-note"
    note.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M14 5l7 7-7 7M21 12H3"/></svg>'

    const text = document.createElement("span")
    text.textContent = "Forwarded to "

    const who = document.createElement("strong")
    who.textContent = recipients.join(", ")
    text.appendChild(who)
    text.appendChild(document.createTextNode(" · just now"))

    note.appendChild(text)
    timeline.prepend(note)
  }

  private applyState(data: ForwardResponse): void {
    if (!data.flags) return

    this.swapFlag(document.querySelector(".inbox-drawer.open .inbox-drawer-state"), data.flags.drawer)
    const row = this.chainId
      ? document.querySelector<HTMLElement>(`.inbox-row[data-chain-id="${CSS.escape(this.chainId)}"]`)
      : null
    if (row) {
      this.swapFlag(row.querySelector(".inbox-state-toggle"), data.flags.row)
      if (data.response_state) row.setAttribute("data-response-state", data.response_state)
    }

    if (!data.previous_state || !data.response_state || data.previous_state === data.response_state) return

    shiftStateCount(data.previous_state, -1)
    shiftStateCount(data.response_state, 1)
    refreshSubtitle()

    const activeState = document.querySelector<HTMLElement>(".inbox-tab.active")?.dataset.responseState
    if (row && activeState && activeState !== data.response_state) this.removeRow(row)
  }

  private removeRow(row: HTMLElement): void {
    const list = row.closest(".inbox-response-list")
    detachRow(row)
    if (!list) return

    if (list.querySelector(".inbox-row")) {
      lowerPagerSummary(list)
    } else {
      const empty = document.createElement("div")
      empty.className = "no-results"
      empty.textContent = "There are no email responses matching these criterias"
      list.replaceWith(empty)
    }
  }

  private swapFlag(current: Element | null, html: string): void {
    if (!current) return

    const template = document.createElement("template")
    template.innerHTML = html.trim()
    const fresh = template.content.firstElementChild
    if (fresh) current.replaceWith(fresh)
  }
}
